import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Mail, CheckCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerificationPending = () => {
  const [searchParams] = useSearchParams();
  const emailFromParams = searchParams.get('email') || '';
  
  const [email, setEmail] = useState(emailFromParams);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const handleResendVerification = async () => {
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setResending(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification-public`, {
        email: email
      });
      setResent(true);
      toast.success('Verification email sent! Please check your inbox.');
    } catch (err) {
      toast.error('Failed to resend verification email. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-blue-50 p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
            <Mail className="h-8 w-8 text-purple-600" />
          </div>
          <CardTitle className="text-2xl">Check Your Email</CardTitle>
          <CardDescription className="text-base">
            We've sent a verification link to your email address. Please click the link to verify your account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Email display/input */}
          {emailFromParams ? (
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-sm text-gray-600 mb-1">Verification sent to:</p>
              <p className="font-medium text-gray-900">{emailFromParams}</p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-gray-600">Enter your email to resend verification:</p>
              <Input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11"
              />
            </div>
          )}

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Next Steps:</h4>
            <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
              <li>Check your inbox (and spam folder)</li>
              <li>Click the verification link in the email</li>
              <li>Once verified, you can sign in to your account</li>
            </ol>
          </div>

          {/* Resend button */}
          {resent ? (
            <div className="flex items-center justify-center gap-2 text-green-600 bg-green-50 rounded-lg p-3">
              <CheckCircle className="h-5 w-5" />
              <span>Verification email sent!</span>
            </div>
          ) : (
            <Button
              onClick={handleResendVerification}
              disabled={resending}
              variant="outline"
              className="w-full h-11"
            >
              {resending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Resend Verification Email
                </>
              )}
            </Button>
          )}

          {/* Back to login */}
          <div className="text-center pt-2">
            <Link 
              to="/auth" 
              className="inline-flex items-center text-sm text-purple-600 hover:text-purple-800 hover:underline"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Sign In
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerificationPending;
